/**
 * Chat Request Utilities
 *
 * Utility functions for constructing chat API request bodies with proper
 * video timing injection. These help reduce duplication across chat components
 * that need to include fresh timing data in their requests.
 */

import { isTimingValidForCaptions } from "./timing-validation";

// =============================================================================
// TYPES
// =============================================================================

/**
 * Type for the getSerializedTiming function
 */
export type GetSerializedTimingFn = () => Record<string, unknown> | null;

/**
 * Minimal message shape expected by the cleaning utility.
 * Matches the AI SDK UIMessage structure without importing it directly.
 */
interface ChatMessage {
  role: string;
  content?: string;
  parts?: Array<{
    type?: string;
    text?: string;
    state?: string;
    approval?: { approved?: boolean };
  }>;
  toolInvocations?: unknown;
}

/** Return true for a complete AI SDK 7 human-approval response part. */
export function isCompletedToolApprovalPart(part: {
  type?: string;
  state?: string;
  approval?: { approved?: boolean };
}): boolean {
  return (
    typeof part.type === "string" &&
    part.type.startsWith("tool-") &&
    part.state === "approval-responded" &&
    typeof part.approval?.approved === "boolean"
  );
}

/**
 * Options for creating a request body with timing
 */
export interface WithTimingOptions {
  /** Existing body to merge with */
  body?: Record<string, unknown> | null;
  /** Function to get fresh serialized timing */
  getSerializedTiming: GetSerializedTimingFn;
  /** Project ID to include in the body */
  projectId?: string | null;
}

/**
 * Creates a request body with fresh video timing data merged in.
 *
 * This utility ensures that video timing is always fetched fresh at request time
 * (not from a stale closure) and properly merged with any existing body options.
 *
 * @param options - Configuration for building the request body
 * @returns A new object with the merged body and fresh timing
 *
 * @example
 * ```ts
 * const body = withFreshTiming({
 *   body: options?.body,
 *   getSerializedTiming,
 *   projectId,
 * });
 * sendMessage(msg, { body });
 * ```
 */
export function withFreshTiming(
  options: WithTimingOptions,
): Record<string, unknown> {
  const { body, getSerializedTiming, projectId } = options;

  // Get fresh timing at call time - critical for post-split scenarios
  const freshTiming = getSerializedTiming();

  return {
    ...(body ?? {}),
    videoOverlayTiming: freshTiming,
    // Only include projectId if it's truthy
    ...(projectId != null ? { projectId } : {}),
  };
}

/**
 * Extracts video overlay timing from a potentially nested request body.
 *
 * Handles the case where timing data may be in the top-level body or
 * nested within a `body.body` structure (common in API forwarding).
 *
 * @param body - The request body to extract timing from
 * @returns The video overlay timing object or null if not found
 */
export function extractVideoOverlayTiming(
  body: Record<string, unknown>,
): Record<string, unknown> | null {
  // Check top-level first
  if (body.videoOverlayTiming != null) {
    return body.videoOverlayTiming as Record<string, unknown>;
  }

  // Check nested body
  const nestedBody = body.body;
  if (nestedBody != null && typeof nestedBody === "object") {
    const nested = nestedBody as Record<string, unknown>;
    if (nested.videoOverlayTiming != null) {
      return nested.videoOverlayTiming as Record<string, unknown>;
    }
  }

  return null;
}

/**
 * Validates that video timing data has sufficient information for caption generation.
 *
 * This performs a thorough check to ensure the timing data represents a valid
 * video segment with meaningful duration. The validation requires BOTH:
 * 1. An overlay ID (indicating a video is selected on the timeline)
 * 2. Valid duration/timing data (durationSeconds, durationInFrames, or video time range)
 *
 * This prevents caption generation from being triggered when:
 * - No video is selected on the timeline
 * - A video exists but isn't explicitly selected by the user
 *
 * NOTE: The underlying logic lives in `lib/utils/timing-validation.ts`
 * (`isTimingValidForCaptions`). This function delegates to it so all timing
 * validity checks share a single implementation. This wrapper is preserved
 * for backward compatibility — existing callers import this name.
 *
 * @param timing - The video timing object to validate
 * @param minDurationSeconds - Minimum required duration in seconds (default: 0.1)
 * @returns True if the timing is valid for caption generation
 */
export function isValidVideoTimingForCaptions(
  timing: Record<string, unknown> | null | undefined,
  minDurationSeconds: number = 0.1,
): boolean {
  return isTimingValidForCaptions(timing, minDurationSeconds);
}

// =============================================================================
// MESSAGE CLEANING
// =============================================================================

/**
 * Strips tool invocations from chat messages before sending to the API.
 *
 * This prevents the model from re-executing tools from previous turns.
 * User messages are kept as-is; assistant messages are filtered to retain
 * only text parts (tool call/result parts are removed). Assistant messages
 * that contained only tool invocations are dropped entirely.
 *
 * This logic was previously duplicated in `chat.tsx` and `aura-chat-content.tsx`.
 *
 * @param messages - The array of chat messages to clean
 * @returns A new array with tool invocations removed
 */
export function cleanChatMessagesForAPI<T extends ChatMessage>(
  messages: T[],
): T[] {
  return (messages || [])
    .map((m) => {
      // AI SDK 7 rejects system messages in the messages array. Trusted
      // instructions are supplied server-side through the instructions option.
      if (m.role === "system" || m.role === "tool") {
        return null;
      }

      // Keep user messages as-is (they're just text)
      if (m.role === "user") {
        return m;
      }

      // For assistant messages, keep text plus completed approval responses.
      if (m.role === "assistant") {
        if (Array.isArray(m.parts)) {
          const textParts = m.parts.filter(
            (p) => p?.type === "text" && typeof p.text === "string",
          );
          const approvalParts = m.parts.filter(isCompletedToolApprovalPart);
          const retainedParts = [...textParts, ...approvalParts];

          // Other historical tool state is intentionally removed to avoid
          // replaying completed tools.
          if (retainedParts.length > 0) {
            return {
              ...m,
              parts: retainedParts,
              toolInvocations: undefined,
            };
          }
          // Skip assistant messages that were tool-only
          return null;
        }
        // If message has content string, keep it
        if (typeof m.content === "string" && m.content.trim()) {
          return {
            ...m,
            toolInvocations: undefined,
          };
        }
        // Skip empty assistant messages
        return null;
      }

      return m;
    })
    .filter(Boolean) as T[];
}

/**
 * Keep only conversation roles accepted by AI SDK 7 UI/model message flows.
 * Client-provided system instructions are intentionally discarded; the server
 * owns the trusted top-level instructions prompt.
 */
export function filterConversationMessages<T extends { role: string }>(
  messages: T[],
): T[] {
  return messages.filter(
    (message) => message.role === "user" || message.role === "assistant",
  );
}

// =============================================================================
// TYPED HTTP ERROR
// =============================================================================

/**
 * An Error subclass that carries an HTTP status code.
 *
 * Use this instead of `(error as any).statusCode` to preserve type-safety
 * when propagating HTTP errors from fetch responses.
 *
 * @example
 * ```ts
 * const err = new HttpError("Not Found", 404);
 * err.statusCode; // 404
 * err instanceof HttpError; // true
 * err instanceof Error;     // true
 * ```
 */
export class HttpError extends Error {
  public readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = "HttpError";
    this.statusCode = statusCode;
  }
}
